import { Selector } from '@ngxs/store';
import { EnumSvSidebarSection } from 'apps/orion/src/app/models/supply-visibility.model';
import {
  Carriers,
  CommitTransactionDetails,
  Countries,
  Reason,
  TransportType,
} from '../../models/commit.model';
import {
  ChildPNInfo,
  Forecast,
  ForecastDetail,
  ForecastWeekRange,
} from '../../models/forecast.model';
import { BuyersPartnumbers } from '../../models/mm-buyers-partnumbers.model';
import { MaterialManagementViews } from '../../models/new-mm-view.model';
import { PartNumberTransactionDetails } from '../../models/partnumbers-list.model';
// import { QuotationTransactionDetails } from '../../models/quotation.model';
import {
  BasicParameters,
  Commit,
  DateRangeParameters,
  DefaultDashboardSettings,
  DummyCommitHeader,
  PurchaseOrders,
  UserSettings,
  VendorParametersFilter,
} from '../../models/supply-visibility.model';
import { SVComments } from '../../models/sv-comments.model';
import { SVNotes } from '../../models/sv-notes.model';
import { SVNotification } from '../../models/sv-notifications.model';
import { SVPredefinedComments } from '../../models/sv-predefined-comments.model';
import { VendorCode } from '../../models/vendor-codes-rights.model';
import { VendorTransactionDetails } from '../vendors-list/vendors-list.types';
import { SupplyVisibilityState } from './supply-visibility.state';
import { SupplyVisibilityStateModel } from './supply-visibility.types';

export class SVSelectors {
  @Selector([SupplyVisibilityState])
  static getCommits(state: SupplyVisibilityStateModel): Commit[] {
    return state?.commits || [];
  }

  @Selector([SupplyVisibilityState])
  static getMaterialManagementViews(state: SupplyVisibilityStateModel): MaterialManagementViews[] {
    return state?.materialManagementViews || [];
  }

  @Selector([SupplyVisibilityState])
  static getVendorTransactionDetails(
    state: SupplyVisibilityStateModel,
  ): VendorTransactionDetails[] {
    return state?.vendorTransactionDetails || [];
  }

  @Selector([SupplyVisibilityState])
  static getPartNumberTransactionDetails(
    state: SupplyVisibilityStateModel,
  ): PartNumberTransactionDetails[] {
    return state?.partnumberTransactionDetails || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitTransactionDetails(
    state: SupplyVisibilityStateModel,
  ): CommitTransactionDetails[] {
    return state?.commitTransactionDetails || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitFilterAdditions(state: SupplyVisibilityStateModel): unknown[] {
    return (state?.commitFilterAdditions as unknown[]) || [];
  }

  @Selector([SupplyVisibilityState])
  static getBasicParameters(state: SupplyVisibilityStateModel): BasicParameters {
    return state?.basicParameters;
  }

  @Selector([SupplyVisibilityState])
  static getPartNumberListParameters(
    state: SupplyVisibilityStateModel,
  ): (widgetId: string) => BasicParameters[] {
    return (widgetId: string): BasicParameters[] => {
      return state?.partNumberListParameters?.[widgetId] || [];
    };
  }

  @Selector([SupplyVisibilityState])
  static getForecast(state: SupplyVisibilityStateModel): Forecast {
    return state?.forecast;
  }

  @Selector([SupplyVisibilityState])
  static getCommitsCarriers(state: SupplyVisibilityStateModel): Carriers[] | undefined {
    return state?.cachedCommitsCarriers || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitsCountries(state: SupplyVisibilityStateModel): Countries[] | undefined {
    return state?.cachedCommitsCountries || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitsTransportType(state: SupplyVisibilityStateModel): TransportType[] | undefined {
    return state?.cachedCommitsTransportType || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitsReasons(state: SupplyVisibilityStateModel): Reason[] | undefined {
    return state?.cachedCommitsReasons || [];
  }

  @Selector([SupplyVisibilityState])
  static getReadOnlyFields(state: SupplyVisibilityStateModel): string[] | undefined {
    return state?.cachedReadOnlyFields;
  }

  @Selector([SupplyVisibilityState])
  static getVendorCodes(state: SupplyVisibilityStateModel): VendorCode[] {
    return state?.vendorCodes || [];
  }

  @Selector([SupplyVisibilityState])
  static getDummyCommitsHeaders(state: SupplyVisibilityStateModel): DummyCommitHeader[] {
    return state?.dummyCommitsHeaders || [];
  }

  @Selector([SupplyVisibilityState])
  static getSelectedDummyCommitsHeader(state: SupplyVisibilityStateModel): DummyCommitHeader {
    return state?.selectedDummyCommitHeader;
  }

  @Selector([SupplyVisibilityState])
  static getForecastDetailsFiltered(state: SupplyVisibilityStateModel): ForecastDetail[] {
    return (state?.forecast?.projectionDetails || [])
      .filter(
        (item: ForecastDetail): boolean => item.week >= (state?.forecastWeekRange?.startWeek || 0),
      )
      .filter(
        (item: ForecastDetail): boolean => item.week <= (state?.forecastWeekRange?.endWeek || 0),
      );
  }

  @Selector([SupplyVisibilityState])
  static getForecastWeekRange(state: SupplyVisibilityStateModel): ForecastWeekRange {
    return state?.forecastWeekRange;
  }

  @Selector([SupplyVisibilityState])
  static getSVComments(state: SupplyVisibilityStateModel): SVComments[] {
    return state?.svComments || [];
  }

  @Selector([SupplyVisibilityState])
  static getSVPredefinedComments(state: SupplyVisibilityStateModel): SVPredefinedComments[] {
    return state?.svPredefinedComments || [];
  }

  @Selector([SupplyVisibilityState])
  static getSVNotes(state: SupplyVisibilityStateModel): SVNotes[] {
    return state?.svNotes || [];
  }

  @Selector([SupplyVisibilityState])
  static getSVNotesCount(state: SupplyVisibilityStateModel): number {
    return state?.svNotesCount || 0;
  }

  @Selector([SupplyVisibilityState])
  static getUserAction(state: SupplyVisibilityStateModel): EnumSvSidebarSection {
    return state?.userAction;
  }

  @Selector([SupplyVisibilityState])
  static getBuyersPartNumberList(state: SupplyVisibilityStateModel): BuyersPartnumbers[] {
    return state?.buyersPartNumbers || [];
  }

  @Selector([SupplyVisibilityState])
  static getChildrenPNList(state: SupplyVisibilityStateModel): Forecast[] {
    return (state?.childrenPNList as Forecast[]) || [];
  }

  @Selector([SupplyVisibilityState])
  static getChildrenPNInfo(state: SupplyVisibilityStateModel): ChildPNInfo | undefined {
    return state?.childrenPNInfo;
  }

  @Selector([SupplyVisibilityState])
  static getCachedChildrenPNList(state: SupplyVisibilityStateModel): ChildPNInfo | undefined {
    return state?.cachedChildrenPNInfo;
  }

  @Selector([SupplyVisibilityState])
  static getBuyersPartNumberListFilterByArg(
    state: SupplyVisibilityStateModel,
  ): (vendorsPartNumbers: VendorParametersFilter) => BuyersPartnumbers[] {
    return (vendorsPartNumbers: VendorParametersFilter): BuyersPartnumbers[] => {
      let data = state?.buyersPartNumbers || [];
      if (vendorsPartNumbers) {
        if (vendorsPartNumbers.partNumbers && vendorsPartNumbers.partNumbers.length > 0) {
          data = data.filter(
            (c: BuyersPartnumbers): boolean =>
              vendorsPartNumbers.partNumbers.indexOf(c.partNo) >= 0,
          );
        }
        if (vendorsPartNumbers.vendors && vendorsPartNumbers.vendors.length > 0) {
          data = data.filter(
            (c: BuyersPartnumbers): boolean =>
              vendorsPartNumbers.vendors.indexOf(c.vendorCode) >= 0,
          );
        }
      }
      return data;
    };
  }

  @Selector([SupplyVisibilityState])
  static getDefaultDashboard(state: SupplyVisibilityStateModel): DefaultDashboardSettings {
    return state?.DefaultDashboardData;
  }

  @Selector([SupplyVisibilityState]) // TODO: Fix type
  static getPlantSpecificLeadDate(state: SupplyVisibilityStateModel): string[] {
    return state?.commitMandatoryDates || [];
  }

  @Selector([SupplyVisibilityState])
  static getCommitStatus(state: SupplyVisibilityStateModel): unknown {
    return state?.commitStatus;
  }

  @Selector([SupplyVisibilityState])
  static getCommitVendorCode(state: SupplyVisibilityStateModel): string {
    return state?.commitVendorCode || '';
  }

  @Selector([SupplyVisibilityState])
  static getAdditionalFilters(state: SupplyVisibilityStateModel): unknown {
    return state?.additionalFilters;
  }

  @Selector([SupplyVisibilityState])
  static getCommitFilter(state: SupplyVisibilityStateModel): unknown {
    return state?.commitFilter;
  }

  @Selector([SupplyVisibilityState])
  static getDaterangeParameters(state: SupplyVisibilityStateModel): DateRangeParameters {
    return state?.daterangeParameters;
  }

  @Selector([SupplyVisibilityState])
  static getPurchaseOrders(state: SupplyVisibilityStateModel): PurchaseOrders[] {
    return state?.purchaseOrders || [];
  }

  @Selector([SupplyVisibilityState])
  static getUserSettings(state: SupplyVisibilityStateModel): UserSettings {
    return state?.userSettings;
  }

  @Selector([SupplyVisibilityState])
  static getNotificationsList(state: SupplyVisibilityStateModel): SVNotification[] {
    return state?.notifications || [];
  }

  @Selector([SupplyVisibilityState])
  static getNotificationsUnreadCount(state: SupplyVisibilityStateModel): number {
    return state?.notificationsUnreadCount || 0;
  }
}
